import { apiClient, safeApi, ApiResponse } from '../../utils/axiosWithAuth';

// =============================================
// TYPES for Menu Billing (Restaurant POS)
// =============================================

export interface MenuCategoryBilling {
  uuid: string;
  clientId: string;
  storeId?: string;
  code: string;
  name: string;
  description?: string;
  image?: string;
  status: string;
}

export interface MenuItemBilling {
  uuid: string;
  clientId: string;
  storeId: string;
  item_code: string;
  item_name: string;
  description?: string;
  categoryId?: string;
  categoryName?: string;
  price: number;
  image?: string;
  type?: string;
  status: string;
}

export interface MenuCartItem {
  menuItemId: string;
  itemCode: string;
  itemName: string;
  quantity: number;
  unitPrice: number;
  originalPrice?: number;
  discount: number;
  tax: number;
  categoryId?: string;
  categoryName?: string;
  image?: string;
  description?: string;
}

export interface MenuCartTotals {
  subtotal: number;
  totalDiscount: number;
  totalTax: number;
  totalAmount: number;
  totalQuantity: number;
}

export interface MenuDailySummary {
  totalOrders: number;
  totalRevenue: number;
  totalTax: number;
  totalDiscount: number;
  avgOrderAmount: number;
  totalBills: number;
}

export interface CreateMenuOrder {
  storeId?: string;
  customerId?: string;
  customerName?: string;
  customerPhone?: string;
  items: {
    menuItemId: string;
    itemCode: string;
    itemName: string;
    quantity: number;
    unitPrice: number;
    discount?: number;
    tax?: number;
    categoryId?: string;
    categoryName?: string;
    image?: string;
  }[];
  paidAmount: number;
  paymentType: string;
  notes?: string;
  billDate?: string;
  taxRate?: number;
  isCreditSale?: boolean;
  creditDueDate?: string;
}

// =============================================
// API FUNCTIONS
// =============================================

export const fetchMenuCategoriesBillingAPI = () =>
  safeApi<MenuCategoryBilling[]>(() =>
    apiClient.get<ApiResponse<MenuCategoryBilling[]>>('/menu-billing/categories')
  );

export const fetchMenuItemsBillingAPI = (params: {
  categoryId?: string;
  search?: string;
  page?: number;
  limit?: number;
} = {}) =>
  safeApi<MenuItemBilling[]>(() => {
    const queryParams = new URLSearchParams();
    if (params.categoryId) queryParams.append('categoryId', params.categoryId);
    if (params.search) queryParams.append('search', params.search);
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());

    return apiClient.get<ApiResponse<MenuItemBilling[]>>(
      `/menu-billing/items${queryParams.toString() ? `?${queryParams.toString()}` : ''}`
    );
  });

export const searchMenuItemsBillingAPI = (query: string, categoryId?: string) =>
  safeApi<MenuItemBilling[]>(() => {
    const queryParams = new URLSearchParams();
    queryParams.append('q', query);
    if (categoryId) queryParams.append('categoryId', categoryId);
    return apiClient.get<ApiResponse<MenuItemBilling[]>>(
      `/menu-billing/search?${queryParams.toString()}`
    );
  });

export const placeMenuOrderAPI = (dto: CreateMenuOrder) =>
  safeApi<{ bill: any; items: any[] }>(() =>
    apiClient.post<ApiResponse<{ bill: any; items: any[] }>>('/menu-billing/orders', dto)
  );

export const fetchMenuDailySummaryAPI = (date?: string) =>
  safeApi<MenuDailySummary>(() => {
    const queryParams = date ? `?date=${date}` : '';
    return apiClient.get<ApiResponse<MenuDailySummary>>(`/menu-billing/reports/daily-summary${queryParams}`);
  });
